// Room templates generating default work items based on type, scope, finish level.
// kind: 'material' | 'labour' | 'mixed'

import { effectiveRate } from "@/lib/regionalPresets";

const FINISH_MULT = { budget: 0.7, mid: 1.0, premium: 1.6 };
const SCOPE_FACTOR = { none: 0, cosmetic: 0.4, moderate: 0.75, full: 1.0 };

// Condition acts as a layer ON TOP of scope/finish — it shifts which items
// are included, how much labour is needed, and whether demolition / rubble
// is required. The same `cosmetic` scope on a `poor` room should still
// include the things a `good` room wouldn't (cracked fixtures, peeling
// surfaces, etc.).
//
//   qtyMult       — multiplier applied to every item's quantity. Captures
//                   the reality that a worn room takes longer to prep.
//   scopeBoost    — shifts each item's `scopeInclude` threshold by N steps
//                   in `SCOPE_ORDER`. +1 = include more items than usual,
//                   -1 = skip the "marginal" items.
//   extras        — items always added on top of the template (deduped
//                   against any existing items by name).
//   skipItems     — names of items to drop even if scope would normally
//                   include them.
const CONDITION_PROFILE = {
  good: {
    qtyMult: 0.85,
    scopeBoost: -1,
    extras: [],
    skipItems: ["Demolition"],
  },
  tired: {
    qtyMult: 1.0,
    scopeBoost: 0,
    extras: [],
    skipItems: [],
  },
  poor: {
    qtyMult: 1.3,
    scopeBoost: 1,
    extras: [
      // Force-include demo + rubble at every scope — a poor-condition
      // room WILL need stripping back regardless of how light the
      // refresh is. baseRate is in USD; effectiveRate() converts.
      { name: "Demolition",       kind: "labour",   unit: "room", baseRate: 1200, qty: 1 },
      { name: "Rubble Removal",   kind: "labour",   unit: "load", baseRate: 1500, qty: 1 },
      { name: "Surface Repairs",  kind: "labour",   unit: "sqm",  baseRate: 80,   qty: (s) => round(Math.max(2, s * 1.5)) },
    ],
    skipItems: [],
  },
};

// Items include: name, kind, unit, baseRate (mid), qty(size) fn or number
// scopeInclude: minimum scope needed to auto-include it
const TEMPLATES = {
  bathroom: [
    { name: "Demolition",     kind: "labour",   unit: "room", baseRate: 800,  qty: 1,                        scopeInclude: "moderate" },
    { name: "Waterproofing",  kind: "material", unit: "sqm",  baseRate: 120,  qty: (s) => round(s * 1.2),    scopeInclude: "moderate" },
    { name: "Tiling",         kind: "mixed",    unit: "sqm",  baseRate: 180,  qty: (s) => round(s * 2.5),    scopeInclude: "cosmetic" },
    { name: "Sanitaryware",   kind: "material", unit: "set",  baseRate: 2500, qty: 1,                        scopeInclude: "moderate" },
    { name: "Vanity",         kind: "material", unit: "item", baseRate: 1800, qty: 1,                        scopeInclude: "cosmetic" },
    { name: "Paint",          kind: "mixed",    unit: "sqm",  baseRate: 35,   qty: (s) => round(s * 2.5),    scopeInclude: "cosmetic" },
    { name: "Lighting",       kind: "material", unit: "item", baseRate: 400,  qty: 2,                        scopeInclude: "cosmetic" },
  ],
  kitchen: [
    { name: "Demolition",     kind: "labour",   unit: "room", baseRate: 1200, qty: 1,                        scopeInclude: "moderate" },
    { name: "Cabinetry",      kind: "material", unit: "lm",   baseRate: 1200, qty: (s) => round(Math.max(4, s * 0.8)), scopeInclude: "cosmetic" },
    { name: "Benchtop",       kind: "material", unit: "lm",   baseRate: 650,  qty: (s) => round(Math.max(3, s * 0.6)), scopeInclude: "cosmetic" },
    { name: "Splashback",     kind: "material", unit: "sqm",  baseRate: 220,  qty: (s) => round(s * 0.6),    scopeInclude: "cosmetic" },
    { name: "Appliances",     kind: "material", unit: "set",  baseRate: 4500, qty: 1,                        scopeInclude: "moderate" },
    { name: "Plumbing",       kind: "labour",   unit: "item", baseRate: 900,  qty: 1,                        scopeInclude: "moderate" },
    { name: "Electrical",     kind: "labour",   unit: "item", baseRate: 850,  qty: 1,                        scopeInclude: "moderate" },
    { name: "Paint",          kind: "mixed",    unit: "sqm",  baseRate: 35,   qty: (s) => round(s * 2.8),    scopeInclude: "cosmetic" },
    { name: "Flooring",       kind: "mixed",    unit: "sqm",  baseRate: 150,  qty: (s) => round(s),          scopeInclude: "moderate" },
  ],
  bedroom: [
    { name: "Flooring",       kind: "mixed",    unit: "sqm",  baseRate: 130, qty: (s) => round(s),           scopeInclude: "moderate" },
    { name: "Paint",          kind: "mixed",    unit: "sqm",  baseRate: 30,  qty: (s) => round(s * 3),       scopeInclude: "cosmetic" },
    { name: "Doors",          kind: "material", unit: "item", baseRate: 480, qty: 1,                         scopeInclude: "moderate" },
    { name: "Wardrobe",       kind: "material", unit: "lm",   baseRate: 600, qty: (s) => round(Math.max(2, s * 0.2)), scopeInclude: "moderate" },
    { name: "Lighting",       kind: "material", unit: "item", baseRate: 220, qty: 2,                         scopeInclude: "cosmetic" },
  ],
  lounge: [
    { name: "Flooring",       kind: "mixed",    unit: "sqm",  baseRate: 135, qty: (s) => round(s),          scopeInclude: "moderate" },
    { name: "Paint",          kind: "mixed",    unit: "sqm",  baseRate: 30,  qty: (s) => round(s * 2.8),    scopeInclude: "cosmetic" },
    { name: "Ceiling",        kind: "mixed",    unit: "sqm",  baseRate: 55,  qty: (s) => round(s),          scopeInclude: "full" },
    { name: "Lighting",       kind: "material", unit: "item", baseRate: 280, qty: 4,                        scopeInclude: "cosmetic" },
    { name: "Skirting/Trim",  kind: "mixed",    unit: "lm",   baseRate: 38,  qty: (s) => round(Math.sqrt(Math.max(1,s)) * 4), scopeInclude: "cosmetic" },
  ],
  laundry: [
    { name: "Cabinetry",      kind: "material", unit: "lm",   baseRate: 900,  qty: 2,                       scopeInclude: "cosmetic" },
    { name: "Benchtop",       kind: "material", unit: "lm",   baseRate: 520,  qty: 2,                       scopeInclude: "moderate" },
    { name: "Tiling",         kind: "mixed",    unit: "sqm",  baseRate: 150,  qty: (s) => round(s * 1.5),   scopeInclude: "moderate" },
    { name: "Plumbing",       kind: "labour",   unit: "item", baseRate: 650,  qty: 1,                       scopeInclude: "moderate" },
    { name: "Paint",          kind: "mixed",    unit: "sqm",  baseRate: 32,   qty: (s) => round(s * 2.2),   scopeInclude: "cosmetic" },
  ],
  hallway: [
    { name: "Flooring",       kind: "mixed",    unit: "sqm",  baseRate: 130, qty: (s) => round(s),          scopeInclude: "moderate" },
    { name: "Paint",          kind: "mixed",    unit: "sqm",  baseRate: 30,  qty: (s) => round(s * 3),      scopeInclude: "cosmetic" },
    { name: "Lighting",       kind: "material", unit: "item", baseRate: 200, qty: 2,                        scopeInclude: "cosmetic" },
  ],
  dining: [
    { name: "Flooring",       kind: "mixed",    unit: "sqm",  baseRate: 135, qty: (s) => round(s),          scopeInclude: "moderate" },
    { name: "Paint",          kind: "mixed",    unit: "sqm",  baseRate: 30,  qty: (s) => round(s * 2.8),    scopeInclude: "cosmetic" },
    { name: "Lighting",       kind: "material", unit: "item", baseRate: 320, qty: 1,                        scopeInclude: "cosmetic" },
  ],
  office: [
    { name: "Flooring",       kind: "mixed",    unit: "sqm",  baseRate: 130, qty: (s) => round(s),          scopeInclude: "moderate" },
    { name: "Paint",          kind: "mixed",    unit: "sqm",  baseRate: 30,  qty: (s) => round(s * 2.8),    scopeInclude: "cosmetic" },
    { name: "Built-in Desk",  kind: "material", unit: "lm",   baseRate: 650, qty: 2,                        scopeInclude: "full" },
    { name: "Lighting",       kind: "material", unit: "item", baseRate: 220, qty: 2,                        scopeInclude: "cosmetic" },
  ],
  other: [
    { name: "Flooring",       kind: "mixed",    unit: "sqm",  baseRate: 130, qty: (s) => round(s),          scopeInclude: "moderate" },
    { name: "Paint",          kind: "mixed",    unit: "sqm",  baseRate: 30,  qty: (s) => round(s * 2.8),    scopeInclude: "cosmetic" },
  ],
};

const SCOPE_ORDER = ["none", "cosmetic", "moderate", "full"];

function round(n) { return Math.max(0, Math.round(n * 10) / 10); }

function scopeGte(a, b) {
  return SCOPE_ORDER.indexOf(a) >= SCOPE_ORDER.indexOf(b);
}

export const ROOM_TYPES = [
  { value: "bathroom", label: "Bathroom" },
  { value: "kitchen", label: "Kitchen" },
  { value: "bedroom", label: "Bedroom" },
  { value: "lounge", label: "Lounge / Living" },
  { value: "dining", label: "Dining" },
  { value: "laundry", label: "Laundry" },
  { value: "hallway", label: "Hallway / Entry" },
  { value: "office", label: "Office / Study" },
  { value: "other", label: "Other" },
];

export const CONDITION_OPTIONS = [
  { value: "good", label: "Good" },
  { value: "tired", label: "Tired" },
  { value: "poor", label: "Poor" },
];

export const SCOPE_OPTIONS = [
  { value: "none", label: "None" },
  { value: "cosmetic", label: "Cosmetic" },
  { value: "moderate", label: "Moderate" },
  { value: "full", label: "Full" },
];

export const FINISH_OPTIONS = [
  { value: "budget", label: "Budget" },
  { value: "mid", label: "Mid" },
  { value: "premium", label: "Premium" },
];

// Build default items for a new room.
// `overrides` (optional) = cost-library room_rates for this room_type:
//   { [itemName: string]: number }  // custom per-item rate in project currency
// `currency` (optional) = project currency; when no override exists we scale
// the USD base rate by the regional currency multiplier so a ZAR project
// doesn't inherit raw USD rates.
export function buildDefaultWorkItems({ room_type, size_sqm, renovation_scope, finish_level, condition = "tired", overrides = {}, currency = "USD" }) {
  if (renovation_scope === "none") return [];
  const tpl = TEMPLATES[room_type] || TEMPLATES.other;
  const finishMult = FINISH_MULT[finish_level] ?? 1;
  const scopeFactor = SCOPE_FACTOR[renovation_scope] ?? 0.75;
  const size = Math.max(0, Number(size_sqm) || 0);
  const condProfile = CONDITION_PROFILE[condition] || CONDITION_PROFILE.tired;

  // Effective scope index, shifted by condition. A "poor" room on
  // `cosmetic` scope behaves like `moderate` for the purpose of which
  // items get included.
  const scopeIdx = SCOPE_ORDER.indexOf(renovation_scope);
  const effIdx = Math.max(0, Math.min(SCOPE_ORDER.length - 1, scopeIdx + condProfile.scopeBoost));
  const effectiveScope = SCOPE_ORDER[effIdx];

  const skipSet = new Set(condProfile.skipItems || []);

  const fromTemplate = tpl
    .filter((item) => !skipSet.has(item.name))
    .filter((item) => scopeGte(effectiveScope, item.scopeInclude))
    .map((item) => buildItem(item, size, scopeFactor, finishMult, condProfile.qtyMult, overrides, currency));

  // Append extras that aren't already present.
  const existingNames = new Set(fromTemplate.map((i) => i.name));
  const extras = (condProfile.extras || [])
    .filter((item) => !existingNames.has(item.name))
    .map((item) => buildItem(item, size, scopeFactor, finishMult, condProfile.qtyMult, overrides, currency));

  return [...fromTemplate, ...extras];
}

function buildItem(item, size, scopeFactor, finishMult, qtyMult, overrides, currency) {
  const baseQty = typeof item.qty === "function" ? item.qty(size) : item.qty;
  // sqm / lm items already scale with size — don't double-discount via
  // scopeFactor. Other units (room, set, item, load) DO get scope-scaled.
  const linearUnit = item.unit === "sqm" || item.unit === "lm";
  const scoped = linearUnit ? baseQty : (baseQty || 0) * scopeFactor;
  const finalQty = round((scoped || 0) * qtyMult);
  const effectiveBase = effectiveRate(item.baseRate, overrides?.[item.name], currency, item.kind);
  return {
    id: cryptoRandomId(),
    name: item.name,
    kind: item.kind,
    quantity: finalQty || baseQty,
    unit: item.unit,
    rate: Math.round(effectiveBase * finishMult),
  };
}

export function emptyWorkItem() {
  return {
    id: cryptoRandomId(),
    name: "New item",
    kind: "material",
    quantity: 1,
    unit: "item",
    rate: 0,
  };
}

function cryptoRandomId() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  return "id-" + Math.random().toString(36).slice(2) + Date.now().toString(36);
}

export { FINISH_MULT, SCOPE_FACTOR };

// Expose a single room-type's template (read-only) so the Add-Item picker
// can populate its "From Library" list.
export function getRoomTemplate(room_type) {
  return TEMPLATES[room_type] || TEMPLATES.other;
}

// Union of every template item across every room type, deduped by name.
// Used by the Add-Item picker's "Common upgrades" section so a user can,
// e.g., add Demolition to a laundry room even though Demolition only lives
// on the kitchen + bathroom templates.
let _commonCache = null;
export function getCommonUpgrades() {
  if (_commonCache) return _commonCache;
  const seen = new Map();
  for (const items of Object.values(TEMPLATES)) {
    for (const it of items) {
      if (!seen.has(it.name)) {
        // Strip the qty function — Common upgrades don't know the room size
        // so the picker will default qty to 1.
        seen.set(it.name, {
          name: it.name,
          kind: it.kind,
          unit: it.unit,
          baseRate: it.baseRate,
        });
      }
    }
  }
  _commonCache = Array.from(seen.values()).sort((a, b) => a.name.localeCompare(b.name));
  return _commonCache;
}
